import { jsonInit } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import {
  serializeTailoredSupplyAppsFilter,
  serializeTailoredSupplyDomainsFilter,
  serializeTailoredSupplyListFilter,
  serializeTailoredSupplyLookupFilter,
  tailoredSupplyAppSchema,
  tailoredSupplyDomainSchema,
  tailoredSupplyItemSchema,
  tailoredSupplyMutationResponseSchema,
  tailoredSupplyPageSchema,
  tailoredSupplyReferenceListSchema,
  type TailoredSupplyAppsFilter,
  type TailoredSupplyCreatePayload,
  type TailoredSupplyDomainsFilter,
  type TailoredSupplyListFilter,
  type TailoredSupplyLookupFilter,
} from './model';

interface TailoredSupplyClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}

const publisherPageSchema = z.looseObject({
  content: z.array(
    z.looseObject({ id: z.union([z.string(), z.number()]).transform(String), name: z.string() }),
  ),
  totalElements: z.number(),
});

export function createTailoredSupplyClient({
  config,
  accessToken,
  fetcher,
}: TailoredSupplyClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);
  return {
    listTailoredSupplies: (filter: TailoredSupplyListFilter) =>
      api.request({
        path: `/api/tailored-supply?${serializeTailoredSupplyListFilter(filter)}`,
        schema: tailoredSupplyPageSchema(tailoredSupplyItemSchema),
      }),
    listTargetingTailoredSupplies: (filter: TailoredSupplyLookupFilter) =>
      api.request({
        path: `/api/tailored-supply?${serializeTailoredSupplyLookupFilter(filter)}`,
        schema: tailoredSupplyPageSchema(tailoredSupplyItemSchema),
      }),
    createTailoredSupply: (payload: TailoredSupplyCreatePayload) =>
      api.request({
        path: '/api/tailored-supply',
        schema: tailoredSupplyMutationResponseSchema,
        init: jsonInit('POST', payload),
      }),
    listApps: (filter: TailoredSupplyAppsFilter) =>
      api.request({
        path: `/api/apps?${serializeTailoredSupplyAppsFilter(filter)}`,
        schema: tailoredSupplyPageSchema(tailoredSupplyAppSchema),
      }),
    listDomains: (filter: TailoredSupplyDomainsFilter) =>
      api.request({
        path: `/api/domains?${serializeTailoredSupplyDomainsFilter(filter)}`,
        schema: tailoredSupplyPageSchema(tailoredSupplyDomainSchema),
      }),
    listPlatforms: () =>
      api.request({ path: '/api/apps/platforms', schema: tailoredSupplyReferenceListSchema }),
    listIabCategories: () =>
      api.request({ path: '/api/iab-categories', schema: tailoredSupplyReferenceListSchema }),
    listPublishers: async () => {
      const publishers: Array<{ id: string; name: string }> = [];
      const size = 100;
      let page = 0;
      let totalElements = 0;
      do {
        const response = await api.request({
          path: `/api/publishers?searchTerm=&page=${String(page)}&size=${String(size)}&sortProperty=name&sortDirection=ASC`,
          schema: publisherPageSchema,
        });
        publishers.push(...response.content);
        totalElements = response.totalElements;
        if (response.content.length === 0) break;
        page += 1;
      } while (publishers.length < totalElements);
      return publishers;
    },
  };
}
